import {useRef, useState} from "react";
import {FtDate} from "@/common/util/FtDate";
import {FtButton} from "@/common/FtButton";

interface CommonDateInputProps<T> {
    required: boolean;
    showNavButtons?: boolean;
    value: T;
    onChange: (value: T) => void;
}

interface OptionalDateInputProps extends CommonDateInputProps<string | undefined> {
    required: false;
}

interface RequiredDateInputProps extends CommonDateInputProps<string> {
    required: true;
}

type DateInputProps = OptionalDateInputProps | RequiredDateInputProps;

export function DateInput(props: DateInputProps) {
    const {value, required, onChange, showNavButtons = false} = props;

    const [displayValue, setDisplayValue] = useState(value ?? "");
    const [lastValidValue, setLastValidValue] = useState(value);
    const dateInputRef = useRef<HTMLInputElement>(null);

    const handleTextBlur = () => {
        if (displayValue === "") {
            if (required) {
                setDisplayValue(lastValidValue ?? "");
            } else {
                setLastValidValue(undefined);
                onChange(undefined);
            }
            return;
        }

        try {
            const normalized = new FtDate(displayValue).ymd();
            setDisplayValue(normalized);
            setLastValidValue(normalized);
            onChange && onChange(normalized);
        } catch (e) {
            setDisplayValue(lastValidValue ?? "");
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setDisplayValue(newValue);
        setLastValidValue(newValue);
        onChange(newValue);
    };

    const openDatePicker = () => {
        dateInputRef.current?.showPicker?.();
        dateInputRef.current?.focus();
    };

    function handleToday() {
        const today = new FtDate().ymd();
        setDisplayValue(today);
        setLastValidValue(today);
        onChange(today);
    }

    function handleIncrease(increment: number) {
        const base = lastValidValue || new FtDate().ymd();
        const next = new FtDate(base).addDay(increment).ymd();
        setDisplayValue(next);
        setLastValidValue(next);
        onChange(next);
    }

    return (
        <div style={{display: "inline-flex", alignItems: "center", position: "relative"}}>
            <input
                type="text"
                placeholder="YYYY-MM-DD"
                value={displayValue}
                onChange={(e) => setDisplayValue(e.target.value)}
                onBlur={handleTextBlur}
                style={{
                    fontSize: "12px",
                    paddingLeft: 4,
                    border: "1px solid #ccc",
                    boxSizing: "border-box",
                    height: 32,
                    borderRadius: "4px 0 0 4px",
                    width: "70px",
                    outline: "none",
                }}
            />

            <button
                type="button"
                onClick={openDatePicker}
                style={{
                    height: 32,
                    background: "#f5f5f5",
                    border: "1px solid #ccc",
                    borderLeft: "none",
                    borderRadius: "0 4px 4px 0",
                    cursor: "pointer",
                    fontSize: "14px",
                }}
            >
                🗓️
            </button>

            {showNavButtons && <>
                <div style={{width: 4}}/>
                <FtButton type="button" style={{width: 30}} variant="outlined" onClick={() => handleIncrease(-1)}>&lt;</FtButton>
                <div style={{width: 4}}/>
                <FtButton type="button" variant="outlined" onClick={() => handleToday()}>오늘</FtButton>
                <div style={{width: 4}}/>
                <FtButton type="button" style={{width: 30}} variant="outlined" onClick={() => handleIncrease(1)}>&gt;</FtButton>
            </>}

            <input
                ref={dateInputRef}
                type="date"
                value={lastValidValue}
                onChange={handleDateChange}
                style={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                    width: 0,
                    height: 0,
                }}
            />
        </div>
    );
}
